export type MoneyEventType =
  | 'receive'
  | 'change_given'
  | 'refund'
  | 'correction_in'
  | 'correction_out';

export interface SettlementUnitInput {
  readonly unitId: string;
  readonly lineId?: string;
  readonly unitIndex?: number;
  readonly amountMinor: number;
}

export interface PaymentOrderTerms {
  readonly orderId: string;
  readonly orderRevision: number;
  readonly currency: string;
  readonly totalMinor: number;
  readonly settlementUnits?: readonly SettlementUnitInput[];
}

export interface PaymentMutationContext {
  readonly writer: string;
  readonly expectedVersion: number;
  readonly idempotencyKey: string;
  readonly operatorId: string;
  readonly at: string;
}

export interface MoneyEvent {
  readonly eventId: string;
  readonly idempotencyKey: string;
  readonly type: MoneyEventType;
  readonly method: string;
  readonly amountMinor: number;
  readonly operatorId: string;
  readonly at: string;
  readonly payerId?: string;
  readonly reference?: string;
  readonly evidenceId?: string;
  readonly reason?: string;
}

export interface PaymentSummary {
  readonly netReceivedMinor: number;
  readonly outstandingMinor: number;
  readonly creditMinor: number;
  readonly refundableMinor: number;
}

export interface SettlementAllocation {
  readonly allocationId: string;
  readonly payerId: string;
  readonly unitId: string;
  readonly lineId?: string;
  readonly unitIndex?: number;
  readonly amountMinor: number;
  readonly orderRevision: number;
  readonly operatorId: string;
  readonly at: string;
}

export interface PayerSettlementSummary {
  readonly allocatedMinor: number;
  readonly netPaidMinor: number;
  readonly outstandingMinor: number;
  readonly creditMinor: number;
}

export type PaymentEvidenceStatus = 'pending' | 'approved' | 'rejected';

export interface PaymentEvidence {
  readonly evidenceId: string;
  readonly method: string;
  readonly storageRef: string;
  readonly reference?: string;
  readonly payerId?: string;
  readonly submittedAt: string;
  readonly status: PaymentEvidenceStatus;
  readonly review?: {
    readonly decision: 'approved' | 'rejected';
    readonly operatorId: string;
    readonly reviewedAt: string;
    readonly amountMinor?: number;
    readonly reason?: string;
  };
}

interface StoredPaymentEvidence {
  readonly evidenceId: string;
  readonly method: string;
  readonly storageRef: string;
  readonly reference?: string;
  readonly payerId?: string;
  readonly submittedAt: string;
  status: PaymentEvidenceStatus;
  review?: {
    readonly decision: 'approved' | 'rejected';
    readonly operatorId: string;
    readonly reviewedAt: string;
    readonly amountMinor?: number;
    readonly reason?: string;
  };
}

export interface PaymentSnapshot {
  readonly orderId: string;
  readonly orderRevision: number;
  readonly currency: string;
  readonly totalMinor: number;
  readonly version: number;
  readonly summary: PaymentSummary;
  readonly fullySettled: boolean;
  readonly unallocatedUnitIds: readonly string[];
  readonly allocations: readonly SettlementAllocation[];
}

export interface PaymentMutationResult {
  readonly version: number;
  readonly summary: PaymentSummary;
  readonly fullySettled: boolean;
}

export interface CashTenderResult extends PaymentMutationResult {
  readonly changeMinor: number;
}

export interface AllocationResult extends PaymentMutationResult {
  readonly allocations: readonly SettlementAllocation[];
}

export interface EvidenceMutationResult extends PaymentMutationResult {
  readonly evidence: PaymentEvidence;
}

interface AcceptedSettlementUnit extends SettlementUnitInput {}

interface IdempotentResult {
  readonly signature: string;
  readonly result: unknown;
}

interface PaymentAccount {
  readonly terms: {
    readonly orderId: string;
    readonly orderRevision: number;
    readonly currency: string;
    readonly totalMinor: number;
    readonly settlementUnits?: readonly AcceptedSettlementUnit[];
  };
  version: number;
  readonly events: MoneyEvent[];
  readonly allocations: SettlementAllocation[];
  readonly evidence: StoredPaymentEvidence[];
  readonly idempotency: Map<string, IdempotentResult>;
}

export interface RecordPaymentCommand extends PaymentMutationContext {
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
}

export interface RecordCashTenderCommand extends PaymentMutationContext {
  readonly tenderedMinor: number;
  readonly payerId?: string;
}

export interface RecordRefundCommand extends PaymentMutationContext {
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
  readonly reason: string;
}

export interface RecordCorrectionCommand extends PaymentMutationContext {
  readonly direction: 'in' | 'out';
  readonly method: string;
  readonly amountMinor: number;
  readonly payerId?: string;
  readonly reference?: string;
  readonly reason: string;
}

export interface AllocateUnitsCommand extends PaymentMutationContext {
  readonly payerId: string;
  readonly unitIds: readonly string[];
}

export interface SubmitPaymentEvidenceCommand extends PaymentMutationContext {
  readonly evidenceId: string;
  readonly method: string;
  readonly storageRef: string;
  readonly reference?: string;
  readonly payerId?: string;
}

export interface ReviewPaymentEvidenceCommand extends PaymentMutationContext {
  readonly decision: 'approved' | 'rejected';
  readonly amountMinor?: number;
  readonly reason?: string;
}

const clone = <T>(value: T): T => structuredClone(value);

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (kind: string, command: unknown): string =>
  JSON.stringify(stableValue({ kind, command }));

const assertTimestamp = (at: string): void => {
  if (!Number.isFinite(Date.parse(at))) throw new Error('PAYMENT_TIMESTAMP_INVALID');
};

const assertPositiveMinor = (amountMinor: number, code = 'PAYMENT_AMOUNT_INVALID'): void => {
  if (!Number.isSafeInteger(amountMinor) || amountMinor <= 0) throw new Error(code);
};

const assertNonNegativeMinor = (amountMinor: number, code: string): void => {
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) throw new Error(code);
};

const assertText = (value: string, code: string): void => {
  if (!value?.trim()) throw new Error(code);
};

const moneySign = (type: MoneyEventType): 1 | -1 =>
  type === 'receive' || type === 'correction_in' ? 1 : -1;

const summarize = (events: readonly MoneyEvent[], totalMinor: number): PaymentSummary => {
  const netReceivedMinor = events.reduce(
    (sum, event) => sum + moneySign(event.type) * event.amountMinor,
    0,
  );
  return {
    netReceivedMinor,
    outstandingMinor: Math.max(0, totalMinor - netReceivedMinor),
    creditMinor: Math.max(0, netReceivedMinor - totalMinor),
    refundableMinor: Math.max(0, netReceivedMinor),
  };
};

const validateTerms = (input: PaymentOrderTerms): PaymentAccount['terms'] => {
  assertText(input.orderId, 'PAYMENT_ORDER_ID_REQUIRED');
  if (!Number.isSafeInteger(input.orderRevision) || input.orderRevision <= 0) {
    throw new Error('PAYMENT_ORDER_REVISION_INVALID');
  }
  assertText(input.currency, 'PAYMENT_CURRENCY_REQUIRED');
  assertNonNegativeMinor(input.totalMinor, 'PAYMENT_TOTAL_INVALID');

  if (input.settlementUnits === undefined) {
    return {
      orderId: input.orderId,
      orderRevision: input.orderRevision,
      currency: input.currency,
      totalMinor: input.totalMinor,
    };
  }

  if (input.settlementUnits.length === 0 && input.totalMinor !== 0) {
    throw new Error('PAYMENT_SETTLEMENT_UNITS_REQUIRED');
  }

  const seen = new Set<string>();
  let total = 0;
  const units = input.settlementUnits.map((unit) => {
    assertText(unit.unitId, 'PAYMENT_SETTLEMENT_UNIT_ID_REQUIRED');
    if (seen.has(unit.unitId)) throw new Error('PAYMENT_SETTLEMENT_UNIT_DUPLICATE');
    seen.add(unit.unitId);
    assertNonNegativeMinor(unit.amountMinor, 'PAYMENT_SETTLEMENT_UNIT_AMOUNT_INVALID');
    if (unit.unitIndex !== undefined && (!Number.isSafeInteger(unit.unitIndex) || unit.unitIndex < 0)) {
      throw new Error('PAYMENT_SETTLEMENT_UNIT_INDEX_INVALID');
    }
    total += unit.amountMinor;
    if (!Number.isSafeInteger(total)) throw new Error('PAYMENT_SETTLEMENT_TOTAL_INVALID');
    return { ...unit };
  });
  if (total !== input.totalMinor) throw new Error('PAYMENT_SETTLEMENT_TOTAL_MISMATCH');

  return {
    orderId: input.orderId,
    orderRevision: input.orderRevision,
    currency: input.currency,
    totalMinor: input.totalMinor,
    settlementUnits: units,
  };
};

const termsSignature = (terms: PaymentAccount['terms']): string =>
  JSON.stringify(stableValue(terms));

export function createPaymentAuthority() {
  const accounts = new Map<string, PaymentAccount>();

  const get = (orderId: string): PaymentAccount => {
    const account = accounts.get(orderId);
    if (!account) throw new Error('PAYMENT_ACCOUNT_NOT_FOUND');
    return account;
  };

  const summaryFor = (account: PaymentAccount): PaymentSummary =>
    summarize(account.events, account.terms.totalMinor);

  const unallocatedUnitIdsFor = (account: PaymentAccount): string[] => {
    if (account.terms.settlementUnits === undefined) return [];
    const allocated = new Set(account.allocations.map((allocation) => allocation.unitId));
    return account.terms.settlementUnits
      .filter((unit) => !allocated.has(unit.unitId))
      .map((unit) => unit.unitId);
  };

  const payerSummaryFor = (account: PaymentAccount, payerId: string): PayerSettlementSummary => {
    const allocatedMinor = account.allocations
      .filter((allocation) => allocation.payerId === payerId)
      .reduce((sum, allocation) => sum + allocation.amountMinor, 0);
    const netPaidMinor = account.events
      .filter((event) => event.payerId === payerId)
      .reduce((sum, event) => sum + moneySign(event.type) * event.amountMinor, 0);
    return {
      allocatedMinor,
      netPaidMinor,
      outstandingMinor: Math.max(0, allocatedMinor - netPaidMinor),
      creditMinor: Math.max(0, netPaidMinor - allocatedMinor),
    };
  };

  const fullySettledFor = (account: PaymentAccount): boolean => {
    const global = summaryFor(account);
    if (global.outstandingMinor !== 0 || global.creditMinor !== 0) return false;
    if (account.terms.settlementUnits === undefined) return true;
    if (unallocatedUnitIdsFor(account).length > 0) return false;

    const payerIds = new Set(account.allocations.map((allocation) => allocation.payerId));
    for (const payerId of payerIds) {
      const payer = payerSummaryFor(account, payerId);
      if (payer.outstandingMinor !== 0 || payer.creditMinor !== 0) return false;
    }
    return true;
  };

  const snapshotFor = (account: PaymentAccount): PaymentSnapshot => ({
    orderId: account.terms.orderId,
    orderRevision: account.terms.orderRevision,
    currency: account.terms.currency,
    totalMinor: account.terms.totalMinor,
    version: account.version,
    summary: summaryFor(account),
    fullySettled: fullySettledFor(account),
    unallocatedUnitIds: unallocatedUnitIdsFor(account),
    allocations: account.allocations.map((allocation) => ({ ...allocation })),
  });

  const assertContext = (command: PaymentMutationContext): void => {
    if (command.writer !== 'smt-local') throw new Error('PAYMENT_WRITER_NOT_ALLOWED');
    if (!Number.isSafeInteger(command.expectedVersion) || command.expectedVersion < 0) {
      throw new Error('PAYMENT_EXPECTED_VERSION_INVALID');
    }
    assertText(command.idempotencyKey, 'PAYMENT_IDEMPOTENCY_KEY_REQUIRED');
    assertText(command.operatorId, 'PAYMENT_OPERATOR_REQUIRED');
    assertTimestamp(command.at);
  };

  const ensurePayerIfAllocated = (account: PaymentAccount, payerId?: string): void => {
    if (account.allocations.length === 0) return;
    if (!payerId) throw new Error('PAYMENT_PAYER_REQUIRED');
    if (!account.allocations.some((allocation) => allocation.payerId === payerId)) {
      throw new Error('PAYMENT_PAYER_NOT_ALLOCATED');
    }
  };

  const appendEvent = (
    account: PaymentAccount,
    command: PaymentMutationContext,
    input: Omit<MoneyEvent, 'eventId' | 'idempotencyKey' | 'operatorId' | 'at'>,
    suffix = '',
  ): MoneyEvent => {
    const event: MoneyEvent = {
      eventId: `${account.terms.orderId}:money:${account.version + 1}:${command.idempotencyKey}${suffix}`,
      idempotencyKey: `${command.idempotencyKey}${suffix}`,
      operatorId: command.operatorId,
      at: command.at,
      ...input,
    };
    account.events.push(event);
    return event;
  };

  const mutate = <T extends Record<string, unknown>>(
    account: PaymentAccount,
    kind: string,
    command: PaymentMutationContext,
    action: () => T,
  ): T & PaymentMutationResult => {
    assertContext(command);
    const signature = signatureOf(kind, command);
    const replay = account.idempotency.get(command.idempotencyKey);
    if (replay) {
      if (replay.signature !== signature) throw new Error('PAYMENT_IDEMPOTENCY_CONFLICT');
      return clone(replay.result as T & PaymentMutationResult);
    }
    if (command.expectedVersion !== account.version) throw new Error('PAYMENT_VERSION_CONFLICT');

    const extra = action();
    account.version += 1;
    const result = {
      ...extra,
      version: account.version,
      summary: summaryFor(account),
      fullySettled: fullySettledFor(account),
    } as T & PaymentMutationResult;
    account.idempotency.set(command.idempotencyKey, { signature, result: clone(result) });
    return clone(result);
  };

  return {
    open(input: PaymentOrderTerms): PaymentSnapshot {
      const accepted = validateTerms(input);
      const existing = accounts.get(accepted.orderId);
      if (existing) {
        if (termsSignature(existing.terms) !== termsSignature(accepted)) {
          throw new Error('PAYMENT_ORDER_TERMS_CONFLICT');
        }
        return snapshotFor(existing);
      }
      const account: PaymentAccount = {
        terms: clone(accepted),
        version: 0,
        events: [],
        allocations: [],
        evidence: [],
        idempotency: new Map(),
      };
      accounts.set(accepted.orderId, account);
      return snapshotFor(account);
    },

    snapshot(orderId: string): PaymentSnapshot {
      return clone(snapshotFor(get(orderId)));
    },

    summary(orderId: string): PaymentSummary {
      return clone(summaryFor(get(orderId)));
    },

    events(orderId: string): MoneyEvent[] {
      return get(orderId).events.map((event) => ({ ...event }));
    },

    evidence(orderId: string): PaymentEvidence[] {
      return get(orderId).evidence.map((entry) => clone(entry));
    },

    payerSummary(orderId: string, payerId: string): PayerSettlementSummary {
      assertText(payerId, 'PAYMENT_PAYER_REQUIRED');
      return clone(payerSummaryFor(get(orderId), payerId));
    },

    allocateUnits(orderId: string, command: AllocateUnitsCommand): AllocationResult {
      const account = get(orderId);
      return mutate(account, 'allocate_units', command, () => {
        assertText(command.payerId, 'PAYMENT_PAYER_REQUIRED');
        if (account.terms.settlementUnits === undefined) throw new Error('PAYMENT_SETTLEMENT_UNITS_NOT_CONFIGURED');
        if (command.unitIds.length === 0) throw new Error('PAYMENT_ALLOCATION_UNITS_REQUIRED');
        const requested = new Set<string>();
        const byId = new Map(account.terms.settlementUnits.map((unit) => [unit.unitId, unit]));
        const allocated = new Set(account.allocations.map((allocation) => allocation.unitId));
        const created: SettlementAllocation[] = [];

        for (const unitId of command.unitIds) {
          if (requested.has(unitId)) throw new Error('PAYMENT_ALLOCATION_UNIT_DUPLICATE');
          requested.add(unitId);
          const unit = byId.get(unitId);
          if (!unit) throw new Error('PAYMENT_SETTLEMENT_UNIT_NOT_FOUND');
          if (allocated.has(unitId)) throw new Error('PAYMENT_SETTLEMENT_UNIT_ALREADY_ALLOCATED');
          const allocation: SettlementAllocation = {
            allocationId: `${account.terms.orderId}:allocation:${account.version + 1}:${command.idempotencyKey}:${created.length}`,
            payerId: command.payerId,
            unitId: unit.unitId,
            lineId: unit.lineId,
            unitIndex: unit.unitIndex,
            amountMinor: unit.amountMinor,
            orderRevision: account.terms.orderRevision,
            operatorId: command.operatorId,
            at: command.at,
          };
          account.allocations.push(allocation);
          created.push(allocation);
        }
        return { allocations: created.map((allocation) => ({ ...allocation })) };
      });
    },

    recordPayment(orderId: string, command: RecordPaymentCommand): PaymentMutationResult {
      const account = get(orderId);
      return mutate(account, 'record_payment', command, () => {
        assertText(command.method, 'PAYMENT_METHOD_REQUIRED');
        assertPositiveMinor(command.amountMinor);
        ensurePayerIfAllocated(account, command.payerId);
        appendEvent(account, command, {
          type: 'receive',
          method: command.method,
          amountMinor: command.amountMinor,
          payerId: command.payerId,
          reference: command.reference,
        });
        return {};
      });
    },

    recordCashTender(orderId: string, command: RecordCashTenderCommand): CashTenderResult {
      const account = get(orderId);
      return mutate(account, 'record_cash_tender', command, () => {
        assertPositiveMinor(command.tenderedMinor, 'PAYMENT_CASH_TENDER_INVALID');
        ensurePayerIfAllocated(account, command.payerId);
        const due = command.payerId
          ? payerSummaryFor(account, command.payerId).outstandingMinor
          : summaryFor(account).outstandingMinor;
        if (due <= 0) throw new Error('PAYMENT_NOTHING_OUTSTANDING');
        const changeMinor = Math.max(0, command.tenderedMinor - due);
        appendEvent(account, command, {
          type: 'receive',
          method: 'CASH',
          amountMinor: command.tenderedMinor,
          payerId: command.payerId,
        }, ':receive');
        if (changeMinor > 0) {
          appendEvent(account, command, {
            type: 'change_given',
            method: 'CASH',
            amountMinor: changeMinor,
            payerId: command.payerId,
          }, ':change');
        }
        return { changeMinor };
      });
    },

    recordRefund(orderId: string, command: RecordRefundCommand): PaymentMutationResult {
      const account = get(orderId);
      return mutate(account, 'record_refund', command, () => {
        assertText(command.method, 'PAYMENT_METHOD_REQUIRED');
        assertText(command.reason, 'PAYMENT_REASON_REQUIRED');
        assertPositiveMinor(command.amountMinor);
        ensurePayerIfAllocated(account, command.payerId);
        const refundable = command.payerId
          ? Math.max(0, payerSummaryFor(account, command.payerId).netPaidMinor)
          : summaryFor(account).refundableMinor;
        if (command.amountMinor > refundable) throw new Error('PAYMENT_OUTFLOW_EXCEEDS_REFUNDABLE');
        appendEvent(account, command, {
          type: 'refund',
          method: command.method,
          amountMinor: command.amountMinor,
          payerId: command.payerId,
          reference: command.reference,
          reason: command.reason,
        });
        return {};
      });
    },

    recordCorrection(orderId: string, command: RecordCorrectionCommand): PaymentMutationResult {
      const account = get(orderId);
      return mutate(account, 'record_correction', command, () => {
        assertText(command.method, 'PAYMENT_METHOD_REQUIRED');
        assertText(command.reason, 'PAYMENT_REASON_REQUIRED');
        assertPositiveMinor(command.amountMinor);
        ensurePayerIfAllocated(account, command.payerId);
        if (command.direction === 'out') {
          const refundable = command.payerId
            ? Math.max(0, payerSummaryFor(account, command.payerId).netPaidMinor)
            : summaryFor(account).refundableMinor;
          if (command.amountMinor > refundable) throw new Error('PAYMENT_OUTFLOW_EXCEEDS_REFUNDABLE');
        }
        appendEvent(account, command, {
          type: command.direction === 'in' ? 'correction_in' : 'correction_out',
          method: command.method,
          amountMinor: command.amountMinor,
          payerId: command.payerId,
          reference: command.reference,
          reason: command.reason,
        });
        return {};
      });
    },

    submitEvidence(orderId: string, command: SubmitPaymentEvidenceCommand): EvidenceMutationResult {
      const account = get(orderId);
      return mutate(account, 'submit_evidence', command, () => {
        assertText(command.evidenceId, 'PAYMENT_EVIDENCE_ID_REQUIRED');
        assertText(command.method, 'PAYMENT_METHOD_REQUIRED');
        assertText(command.storageRef, 'PAYMENT_EVIDENCE_STORAGE_REQUIRED');
        if (account.evidence.some((entry) => entry.evidenceId === command.evidenceId)) {
          throw new Error('PAYMENT_EVIDENCE_ID_CONFLICT');
        }
        const evidence: StoredPaymentEvidence = {
          evidenceId: command.evidenceId,
          method: command.method,
          storageRef: command.storageRef,
          reference: command.reference,
          payerId: command.payerId,
          submittedAt: command.at,
          status: 'pending',
        };
        account.evidence.push(evidence);
        return { evidence: clone(evidence) };
      });
    },

    reviewEvidence(orderId: string, evidenceId: string, command: ReviewPaymentEvidenceCommand): EvidenceMutationResult {
      const account = get(orderId);
      return mutate(account, `review_evidence:${evidenceId}`, command, () => {
        const evidence = account.evidence.find((entry) => entry.evidenceId === evidenceId);
        if (!evidence) throw new Error('PAYMENT_EVIDENCE_NOT_FOUND');
        if (evidence.status !== 'pending') throw new Error('PAYMENT_EVIDENCE_ALREADY_REVIEWED');
        if (command.decision === 'approved') {
          if (command.amountMinor === undefined) throw new Error('PAYMENT_APPROVAL_AMOUNT_REQUIRED');
          assertPositiveMinor(command.amountMinor, 'PAYMENT_APPROVAL_AMOUNT_INVALID');
          ensurePayerIfAllocated(account, evidence.payerId);
          appendEvent(account, command, {
            type: 'receive',
            method: evidence.method,
            amountMinor: command.amountMinor,
            payerId: evidence.payerId,
            reference: evidence.reference,
            evidenceId,
          }, `:evidence:${evidenceId}`);
        }
        evidence.status = command.decision;
        evidence.review = {
          decision: command.decision,
          operatorId: command.operatorId,
          reviewedAt: command.at,
          amountMinor: command.amountMinor,
          reason: command.reason,
        };
        return { evidence: clone(evidence) };
      });
    },
  };
}
